import { z } from "zod";

/**
 * Cliente Creative OS (contexto editorial do tenant / workspace).
 */

export const statusDoClienteSchema = z.enum(["ativo", "inativo"]);

export const clienteSchema = z.object({
  id: z.string().uuid(),
  nome: z.string().min(1),
  slug: z.string().min(1),
  status: statusDoClienteSchema,
  instagramPageId: z.string().nullable(),
  facebookPageId: z.string().nullable(),
  clickupProjectId: z.string().nullable(),
  tomDeVoz: z.string().nullable(),
  posicionamento: z.string().nullable(),
  preferenciasVisuais: z.string().nullable(),
  volumetriaPadrao: z.string().nullable(),
  datasComemorativas: z.string().nullable(),
  notas: z.string().nullable(),
  criadoEm: z.string().datetime(),
  atualizadoEm: z.string().datetime(),
});

export const listaDeClientesSchema = z.object({
  itens: z.array(clienteSchema),
});

export const criarClienteSchema = z.object({
  nome: z.string().trim().min(2).max(200),
  instagramPageId: z.string().trim().max(200).nullable().optional(),
  facebookPageId: z.string().trim().max(200).nullable().optional(),
  clickupProjectId: z.string().trim().max(200).nullable().optional(),
  tomDeVoz: z.string().trim().max(8_000).nullable().optional(),
  posicionamento: z.string().trim().max(8_000).nullable().optional(),
  preferenciasVisuais: z.string().trim().max(8_000).nullable().optional(),
  volumetriaPadrao: z.string().trim().max(8_000).nullable().optional(),
  datasComemorativas: z.string().trim().max(8_000).nullable().optional(),
  notas: z.string().trim().max(8_000).nullable().optional(),
});

export const atualizarClienteSchema = z.object({
  nome: z.string().trim().min(2).max(200).optional(),
  status: statusDoClienteSchema.optional(),
  instagramPageId: z.string().trim().max(200).nullable().optional(),
  facebookPageId: z.string().trim().max(200).nullable().optional(),
  clickupProjectId: z.string().trim().max(200).nullable().optional(),
  tomDeVoz: z.string().trim().max(8_000).nullable().optional(),
  posicionamento: z.string().trim().max(8_000).nullable().optional(),
  preferenciasVisuais: z.string().trim().max(8_000).nullable().optional(),
  volumetriaPadrao: z.string().trim().max(8_000).nullable().optional(),
  datasComemorativas: z.string().trim().max(8_000).nullable().optional(),
  notas: z.string().trim().max(8_000).nullable().optional(),
});

const hexCorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, "cor hex invalida");

export const brandGuidelinesSchema = z.object({
  clienteId: z.string().uuid(),
  tomDeVoz: z.string().nullable(),
  tagsTom: z.array(z.string()),
  paletaCores: z.array(hexCorSchema),
  fontes: z.array(z.string()),
  termosProibidos: z.array(z.string()),
  referenciasVisuais: z.array(z.string().url()),
  publicoAlvo: z.string().nullable(),
  atualizadoEm: z.string().datetime().nullable(),
});

export const atualizarBrandGuidelinesSchema = z.object({
  tomDeVoz: z.string().trim().max(8_000).nullable().optional(),
  tagsTom: z.array(z.string().trim().min(1).max(80)).max(40).optional(),
  paletaCores: z.array(hexCorSchema).max(24).optional(),
  fontes: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
  termosProibidos: z.array(z.string().trim().min(1).max(120)).max(80).optional(),
  referenciasVisuais: z.array(z.string().url().max(2_000)).max(40).optional(),
  publicoAlvo: z.string().trim().max(8_000).nullable().optional(),
});

export const dataComemorativaSchema = z.object({
  id: z.string().uuid(),
  clienteId: z.string().uuid(),
  dia: z.number().int().min(1).max(31),
  mes: z.number().int().min(1).max(12),
  nomeEvento: z.string().min(1),
  querComemorar: z.boolean(),
  tipoAcao: z.string().nullable(),
  especialidadeRelacionada: z.string().nullable(),
  recorrenteAnual: z.boolean(),
  dataUnica: z.string().date().nullable(),
  criadoEm: z.string().datetime(),
  atualizadoEm: z.string().datetime(),
});

export const listaDeDatasComemorativasSchema = z.object({
  itens: z.array(dataComemorativaSchema),
});

export const criarDataComemorativaSchema = z.object({
  dia: z.number().int().min(1).max(31),
  mes: z.number().int().min(1).max(12),
  nomeEvento: z.string().trim().min(2).max(200),
  querComemorar: z.boolean().default(true),
  tipoAcao: z.string().trim().max(200).nullable().optional(),
  especialidadeRelacionada: z.string().trim().max(200).nullable().optional(),
  recorrenteAnual: z.boolean().default(true),
  dataUnica: z.string().date().nullable().optional(),
});

export const atualizarDataComemorativaSchema =
  criarDataComemorativaSchema.partial();

export const distribuicaoVolumetriaSchema = z.object({
  reels: z.number().int().min(0).max(365),
  carrossel: z.number().int().min(0).max(365),
  estatico: z.number().int().min(0).max(365),
  stories: z.number().int().min(0).max(365),
});

export const volumetriaSchema = z.object({
  clienteId: z.string().uuid(),
  totalPostsMes: z.number().int().min(0).max(365),
  distribuicao: distribuicaoVolumetriaSchema,
  responsavelPadraoId: z.string().uuid().nullable(),
  atualizadoEm: z.string().datetime().nullable(),
});

export const atualizarVolumetriaSchema = z.object({
  totalPostsMes: z.number().int().min(0).max(365),
  distribuicao: distribuicaoVolumetriaSchema,
  responsavelPadraoId: z.string().uuid().nullable().optional(),
});

export const dossieSchema = z.object({
  clienteId: z.string().uuid(),
  conteudoJson: z.unknown(),
  conteudoMarkdown: z.string(),
  atualizadoEm: z.string().datetime().nullable(),
  atualizadoPor: z.string().nullable(),
});

export const atualizarDossieSchema = z.object({
  conteudoJson: z.unknown(),
  conteudoMarkdown: z.string().max(200_000),
});

export const dossieVersaoSchema = z.object({
  id: z.string().uuid(),
  clienteId: z.string().uuid(),
  conteudoMarkdown: z.string(),
  autorSujeito: z.string(),
  criadoEm: z.string().datetime(),
});

export const listaDeDossieVersoesSchema = z.object({
  itens: z.array(dossieVersaoSchema),
});

export const modoVinculoMetaSchema = z.enum([
  "instagram",
  "pagina",
  "pagina_e_instagram",
]);

export const metaConexaoSchema = z.object({
  status: z.enum(["conectado", "nao_conectado", "expirado"]),
  instagramPageId: z.string().nullable(),
  facebookPageId: z.string().nullable(),
  facebookPageNome: z.string().nullable(),
  facebookPageFotoUrl: z.string().nullable(),
  instagramUsername: z.string().nullable(),
  instagramFotoUrl: z.string().nullable(),
  tokenExpiraEm: z.string().datetime().nullable(),
  precisaSelecionarPagina: z.boolean(),
  modoVinculo: modoVinculoMetaSchema.nullable(),
});

export const metaPaginaDisponivelSchema = z.object({
  facebookPageId: z.string().min(1),
  nome: z.string().min(1),
  facebookPageFotoUrl: z.string().nullable(),
  instagramPageId: z.string().nullable(),
  instagramUsername: z.string().nullable(),
  instagramFotoUrl: z.string().nullable(),
});
export const listaMetaPaginasSchema = z.object({
  itens: z.array(metaPaginaDisponivelSchema),
});

export const selecionarPaginaMetaSchema = z.object({
  facebookPageId: z.string().min(1),
  modo: modoVinculoMetaSchema.default("instagram"),
});

export const metaHealthSchema = z.object({
  status: z.enum(["connected", "expired", "missing"]),
  expiresInDays: z.number().nullable(),
});

export type Cliente = z.infer<typeof clienteSchema>;
export type ListaDeClientes = z.infer<typeof listaDeClientesSchema>;
export type CriarCliente = z.infer<typeof criarClienteSchema>;
export type AtualizarCliente = z.infer<typeof atualizarClienteSchema>;
export type BrandGuidelines = z.infer<typeof brandGuidelinesSchema>;
export type AtualizarBrandGuidelines = z.infer<
  typeof atualizarBrandGuidelinesSchema
>;
export type DataComemorativa = z.infer<typeof dataComemorativaSchema>;
export type CriarDataComemorativa = z.infer<typeof criarDataComemorativaSchema>;
export type AtualizarDataComemorativa = z.infer<
  typeof atualizarDataComemorativaSchema
>;
export type Volumetria = z.infer<typeof volumetriaSchema>;
export type AtualizarVolumetria = z.infer<typeof atualizarVolumetriaSchema>;
export type Dossie = z.infer<typeof dossieSchema>;
export type AtualizarDossie = z.infer<typeof atualizarDossieSchema>;
export type DossieVersao = z.infer<typeof dossieVersaoSchema>;
export type MetaConexao = z.infer<typeof metaConexaoSchema>;
export type MetaHealth = z.infer<typeof metaHealthSchema>;
export type MetaPaginaDisponivel = z.infer<typeof metaPaginaDisponivelSchema>;
export type SelecionarPaginaMeta = z.infer<typeof selecionarPaginaMetaSchema>;
export type ModoVinculoMeta = z.infer<typeof modoVinculoMetaSchema>;
